import AuthorizationService from "../../services/AuthorizationService";
import CryptoJS from "crypto-js";
import CryptoService from "../../services/CryptoService";
import HttpService, {
  HttpMethod,
  ISearchParams,
} from "../../services/HttpService";
import { ISession } from "../../services/SessionService";
import AppPropsService from "../../services/AppPropsService";
import { IAccountsList } from "../../content/MainContent/MainContent.types";
import staticMethods from "antd/es/message";

export const generateUrl = (
  resourcePath: string,
  searchParams?: ISearchParams | {}
) => {
  const API_URL_PATH = AppPropsService.getVoiceGainAuthSvcApiUrlPath();
  const SERVER_URL = AppPropsService.getVoiceGainServerUrl();  
  return HttpService.generateUrl(
    API_URL_PATH,
    resourcePath,
    SERVER_URL,
    searchParams
  );
}; 

abstract class VoiceGainAuthSvcApi {
  public static acceptTermsOfUse = (
    accountId: ISession["accountId"],
    apiKey: ISession["apiKey"],
    authToken: ISession["authToken"],
    nonce: ISession["nonces"][0],
    userId: ISession["userId"]
  ) => {
    const body = JSON.stringify({
      termsAcceptedBy: userId,
    });
    const signature = CryptoJS.HmacSHA256(
      body.toString(),
      authToken
    ).toString();
    const headers = {
      "Content-Type": "application/json",
      Mac: `signature=${signature}|sessionId=${apiKey}|nonce=${nonce}`,
    };
    const method = HttpMethod.Put;
    const url = generateUrl(`/account/${accountId}`);
    const requestInfo: RequestInfo = url.toString();
    const requestInit: RequestInit = { body, headers, method };
    return fetch(requestInfo, requestInit);
  };

  public static checkResetToken = (resetToken: string) => {
    const method = HttpMethod.Get;
    const url = generateUrl(`/auth/check/${resetToken}`);
    const requestInfo: RequestInfo = url.toString();
    const requestInit: RequestInit = { method };
    return fetch(requestInfo, requestInit);
  };

  public static createNewUser = (
    email: string,
    firstName: string,
    lastName: string,
    token: string,
    requestedFeatures?: string[],
    mainFeature?: string | undefined,
    referrer?: string,
    companyName?: string
  ) => {
    const method = HttpMethod.Post;
    const headers = { "Content-Type": "application/json" };
    const body = JSON.stringify({
      accountType: "ACP",
      businessName: companyName,
      referrer,
      mainMode: mainFeature,
      requestedFeatures: requestedFeatures
        ? requestedFeatures.reduce<any>((acc, it) => {
            acc[it] = "Requested via ACP sign-up";
            return acc;
          }, {})
        : undefined,
    });
    const url = generateUrl("/account", {
      email,
      firstName,
      lastName,
      token,
      tokenType: "reCaptcha",
    });
    const requestInfo: RequestInfo = url.toString();
    const requestInit: RequestInit = { method, headers, body };
    return fetch(requestInfo, requestInit);
  };

  public static fetchLogin = (
    email: string,
    password: string,
    loginUuid: string
  ): Promise<Response> => {
    const url = generateUrl("/auth/login");            
    const body = { email, password, loginUuid };
    const headers = {
      "Content-Type": "application/json",
    };
    const requestInfo: RequestInfo = url.toString();
    const requestInit: RequestInit = {
      body: JSON.stringify(body),
      method: HttpMethod.Post,
      headers,
    };
    return fetch(requestInfo, requestInit);
  };

  public static fetchTotpLogin = (
    twoFAToken: string,
    totpCode: string,
  ): Promise<Response> => {
    const url = generateUrl("/auth/login/totp");            
    const body = { twoFAToken, totpCode };
    const headers = {
      "Content-Type": "application/json",
    };
    const requestInfo: RequestInfo = url.toString();
    const requestInit: RequestInit = {
      body: JSON.stringify(body),
      method: HttpMethod.Put,
      headers,
    };
    return fetch(requestInfo, requestInit);
  };

  public static fetchEmailOtpLogin = (
    twoFAToken: string
  ): Promise<Response> => {
    const url = generateUrl("/auth/login/totp/otp");
    const body = { twoFAToken };
    const headers = {
      "Content-Type": "application/json",
    };
    const requestInfo: RequestInfo = url.toString();
    const requestInit: RequestInit = {
      body: JSON.stringify(body),
      method: HttpMethod.Put,
      headers,
    };
    return fetch(requestInfo, requestInit);
  };

  public static deletePhoneNumber = (
    phoneNumbers: string[],
    uuid: string
  ): Promise<Response> => {
    const url = generateUrl(`/account/${uuid}/phone-number`);
    const body = phoneNumbers;
    const headers = {
      "Content-Type": "application/json",
    };
    const requestInfo: RequestInfo = url.toString();
    const requestInit: RequestInit = {
      body: JSON.stringify(body),
      method: HttpMethod.Delete,
      headers,
    }; 
    return AuthorizationService.authorizedFetch(requestInfo, requestInit);
  };

  public static putEditRateLimits = (
    rateLimits: IAccountsList["limits"],
    uuid: string
  ): Promise<Response> => {
    const url = generateUrl(`/account/${uuid}/limits`);
    const body = rateLimits;
    const headers = {
      "Content-Type": "application/json",
    };
    const requestInfo: RequestInfo = url.toString();
    const requestInit: RequestInit = {
      body: JSON.stringify(body),
      method: HttpMethod.Put,
      headers,
    };
    return AuthorizationService.authorizedFetch(requestInfo, requestInit);
  };

  public static fetchPreLogin = (
    loginUuid: string,
    keyLen: number
  ) => {
    const url = generateUrl("/auth/login/pre");
    const body = { loginUuid, keyLen };
    const headers = {
      "Content-Type": "application/json",
    };
    const requestInfo: RequestInfo = url.toString();
    const requestInit: RequestInit = {
      body: JSON.stringify(body),
      method: HttpMethod.Post,
      headers,
    };
    return fetch(requestInfo, requestInit);
  }

  public static logOut = () => {
    const method = HttpMethod.Post;
    const url = generateUrl("/auth/logout");
    const requestInfo: RequestInfo = url.toString();
    const requestInit: RequestInit = { method };
    return AuthorizationService.authorizedFetch(requestInfo, requestInit);
  };

  public static refillCredit = (
    accountId: string,
    amount: number,
    userId: string
  ) => {
    const body = JSON.stringify({
      amount,
      requestingUser: userId,
    });
    const headers = { "Content-Type": "application/json" };
    const method = HttpMethod.Post;
    const url = generateUrl(`/account/${accountId}/refill`);
    const requestInfo: RequestInfo = url.toString();
    const requestInit: RequestInit = { headers, body, method };
    return AuthorizationService.authorizedFetch(requestInfo, requestInit);
  };

  public static refreshNonces = (nonce: string) => {
    const headers = {
      Mac: CryptoService.generateMac(nonce),
    };
    const method = HttpMethod.Post;
    const url = generateUrl("/auth/refresh");
    const requestInfo: RequestInfo = url.toString();
    const requestInit: RequestInit = { headers, method };
    return fetch(requestInfo, requestInit);
  };

  public static resetPassword = (
    newPassword: string,
    resetToken: string,
    uid: string
  ) => {
    const body = JSON.stringify({
      pwd: newPassword,
      pwdStrength: 2,
      rtok: resetToken,
      uid,
    });
    const method = HttpMethod.Post;
    const url = generateUrl("/auth/reset");
    const requestInfo: RequestInfo = url.toString();
    const requestInit: RequestInit = { body, method };
    return fetch(requestInfo, requestInit);
  };

  //Get all accounts
  public static getAccounts = (inclExtra?: boolean): Promise<Response> => {
    const method = HttpMethod.Get;
    const url = generateUrl("/account", {
      ...(inclExtra && { inclExtra: inclExtra.toString() }),
    });
    const requestInfo: RequestInfo = url.toString();
    const requestInit: RequestInit = { method };
    return AuthorizationService.authorizedFetch(requestInfo, requestInit);
  };

  //Get all accounts including email
  public static getAccountsWithEmails = (): Promise<Response> => {
    const method = HttpMethod.Get;
    const headers = { Accept: "application/json" };
    const url = generateUrl("/account/users", {
      inclExtra: "true",
    });
    const requestInfo: RequestInfo = url.toString();
    const requestInit: RequestInit = { method, headers };
    return AuthorizationService.authorizedFetch(requestInfo, requestInit);
  };

  //Get single account
  public static getSingleAccount = (uuid: string): Promise<Response> => {
    const method = HttpMethod.Get;
    const url = generateUrl(`/account/${uuid}`, { inclExtra: "true" });
    const requestInfo: RequestInfo = url.toString();
    const requestInit: RequestInit = { method };
    return AuthorizationService.authorizedFetch(requestInfo, requestInit);
  };

  //Get CSV export of accounts and users
  public static getCsvAccount = (): Promise<Response> => {
    const method = HttpMethod.Get;
    const url = generateUrl("/account/users");
    const requestInfo: RequestInfo = url.toString();
    const requestInit: RequestInit = { method };
    return AuthorizationService.authorizedFetch(requestInfo, requestInit);
  };

  //Add New Account
  public static addNewAccount = (
    firstName: string,
    lastName: string,
    email: string,
    accountType: string | undefined,
    businessName: string | undefined,
    webName: string | undefined,
    token: string
  ): Promise<Response> => {
    const headers = {
      "Content-Type": "application/json",
    };
    const body = JSON.stringify({
      accountType,
      businessName,
      webName,
    });
    const method = HttpMethod.Post;
    const url = generateUrl("/account", {
      email,
      firstName,
      lastName,
      token,
      tokenType: "reCaptcha",
    });
    const requestInfo: RequestInfo = url.toString();
    const requestInit: RequestInit = { headers, body, method };
    return AuthorizationService.authorizedFetch(requestInfo, requestInit);
  };

  public static deleteAccount = (accountId: string): Promise<Response> => {
    const headers = {
      "Content-Type": "application/json",
    };
    const method = HttpMethod.Delete;
    const url = generateUrl(`/account/${accountId}`);
    const requestInfo: RequestInfo = url.toString();
    const requestInit: RequestInit = { headers, method };
    return AuthorizationService.authorizedFetch(requestInfo, requestInit);
  };

  public static modAccount = (
    accountId: string,
    accountBody: Partial<IAccountsList> 
  ): Promise<Response> => {
    const headers = {
      "Content-Type": "application/json",
    };
    const body = JSON.stringify(accountBody);
    const method = HttpMethod.Put;
    const url = generateUrl(`/account/${accountId}`);
    const requestInfo: RequestInfo = url.toString();
    const requestInit: RequestInit = { headers, body, method };
    return AuthorizationService.authorizedFetch(requestInfo, requestInit);
  };

  public static queryUsers = (): Promise<Response> => {
    const method = HttpMethod.Get;
    const url = generateUrl(`/user`);
    const requestInfo: RequestInfo = url.toString();
    const requestInit: RequestInit = { method };
    return AuthorizationService.authorizedFetch(requestInfo, requestInit);
  };

  public static getUsersFromAccount = (
    accountId: string
  ): Promise<Response> => {
    const method = HttpMethod.Get;
    const url = generateUrl(`/account/${accountId}/users`);
    const requestInfo: RequestInfo = url.toString();
    const requestInit: RequestInit = { method };
    return AuthorizationService.authorizedFetch(requestInfo, requestInit);
  };

  public static modUser = (
    userId: string,
    email: string,
    accountId: string,
    firstName: string,
    lastName: string,
    status: string,
    permissions: string
  ): Promise<Response> => {
    const headers = {
      "Content-Type": "application/json",
    };
    const body = JSON.stringify({
      userId,
      email,
      accountId,
      firstName,
      lastName,
      status,
      permissions,
    });
    const method = HttpMethod.Put;
    const url = generateUrl(`/user/${userId}`);
    const requestInfo: RequestInfo = url.toString();
    const requestInit: RequestInit = { headers, body, method };
    return AuthorizationService.authorizedFetch(requestInfo, requestInit);
  };

  public static deleteUser = (userId: string): Promise<Response> => {
    const method = HttpMethod.Delete;
    const url = generateUrl(`/user/${userId}`);
    const requestInfo: RequestInfo = url.toString();
    const requestInit: RequestInit = { method };
    return AuthorizationService.authorizedFetch(requestInfo, requestInit);
  };

  public static sendResetEmail = (email: string): Promise<Response> => {
    const headers = {
      "Content-Type": "application/json",
    };
    const body = JSON.stringify({ email });
    const method = HttpMethod.Post;
    const url = generateUrl("/auth/forgot");
    const requestInfo: RequestInfo = url.toString();
    const requestInit: RequestInit = { headers, body, method };
    return AuthorizationService.authorizedFetch(requestInfo, requestInit);
  };

  //Recache
  public static recacheFetch = (): Promise<Response> => {
    const method = HttpMethod.Put;
    const url = generateUrl("/account/cache");

    const headers = { "Content-Type": "application/json" };
    const body = JSON.stringify({ regenerateIfOlderThanSeconds: 300 });

    const requestInfo: RequestInfo = url.toString();
    const requestInit: RequestInit = { headers, method, body };
    return AuthorizationService.authorizedFetch(requestInfo, requestInit);
  };
}

export default VoiceGainAuthSvcApi;
